import { useQueryClient } from '@tanstack/react-query';
import { CellContext, RowSelectionState } from '@tanstack/react-table';
import { FC, useState } from 'react';
import Card from '@/components/Card/Card';
import Icon from '@/components/Icons/Icon';
import Table from '@/components/Tables/Table/Table';
import TableCardWithSelectedTable from '@/components/Tables/TableCardWithSelectedTable/TableCardWithSelectedTable';
import Wizard from '@/components/Wizard/Wizard';
import { StepCount } from '@/components/Wizard/Wizard.types';
import { useUpdateGroup } from '@/services/groups/groups.service';
import { useStores } from '@/services/stores/stores.service';
import { Store } from '@/types/stores.types';
import styles from './AddStoresToGroup.module.css';
import { AddStoresToGroupProps } from './AddStoresToGroup.types';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Grid, Stack } from '@mui/material';
import { AlertTriangle } from 'react-feather';
import AddStoresModal from './AddStoresModal/AddStoresModal';
import {
  buildMapGroupNameCountForGroups,
  buildMapGroupNameCountForStores,
  filterMatchingStoreCounts,
} from '@/services/groups/groups.helper.service';
import { AxiosResponse } from 'axios';
import RequestProcessingModal from '@/components/Modal/RequestProcessingModal/RequestProcessingModal';
import { useRequestProcessingModal } from '@/hooks/useRequestProcessingModal';

const alertGroupName = (emptySGs: string[]) => {
  const Cell = ({ row }: CellContext<Store, unknown>) => {
    const { groupName } = row.original;
    const willBeEmpty = !!emptySGs.find((group) => group === groupName);

    return (
      <Stack direction="row" spacing={1}>
        {willBeEmpty && <AlertTriangle size={16} color="#E6740A" />}
        <Stack>{groupName}</Stack>
      </Stack>
    );
  };
  return Cell;
};

const AddStoresToGroup: FC<AddStoresToGroupProps> = ({
  isOpen,
  onClose,
  setConfirmationModalVisible,
  groupId,
  groupName,
  allGroups = [],
  setPartialSuccessData,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<StepCount>(0);
  const [selectedStores, setSelectedStores] = useState<RowSelectionState>({});
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const { mutate, isLoading } = useUpdateGroup(groupId);
  const { data, isLoading: storesIsLoading } = useStores(isOpen);
  const queryClient = useQueryClient();
  const allStores = data?.data || [];
  const { isModalOpen, modalType, showTimeoutModal, showErrorModal, handleCloseAndRefresh } = useRequestProcessingModal(
    { onRefresh: () => window.location.reload() },
  );

  const storesToSelectfrom = allStores.filter((store) => store.groupName !== groupName);

  const mapAllGroupCounts = buildMapGroupNameCountForGroups(allGroups);

  const mapSelectStoresGroupNames = buildMapGroupNameCountForStores(
    Object.keys(selectedStores).map((storeNo) => storesToSelectfrom[+storeNo]),
  );
  const emptyStoreGroups = filterMatchingStoreCounts(mapAllGroupCounts, mapSelectStoresGroupNames);

  const handleClose = () => {
    setSelectedStores({});
    setStep(0);
    onClose();
  };

  const onSuccess = (response: AxiosResponse) => {
    onClose();
    setOpenConfirmationModal(false);
    setStep(0);
    setSelectedStores({});
    const selectedStoresCount = Object.values(selectedStores).filter((val) => val).length;
    queryClient.invalidateQueries(['stores']);

    if (response.status === 207) {
      setPartialSuccessData({
        storesWithErrors: response.data.storeDetails.filter(
          (detail: { status: string; errorMessage: string; storeNo: string }) => detail.status === 'FAILED',
        ),
        storesSubmittedCount: selectedStoresCount,
      });
    } else {
      setConfirmationModalVisible(true);
    }
  };

  const onError = (error: unknown) => {
    if (error instanceof Error && error.message === 'API Gateway request timed out') {
      showTimeoutModal();
      return;
    }
    showErrorModal();
  };

  return (
    <>
      <Wizard
        currentStep={step}
        steps={[
          {
            content: (
              <div className={styles.stepOne}>
                <div className={styles.groupName}>{groupName}</div>
                <div className={styles.selectStores}>
                  <div className={styles.infoBlock}>
                    <Icon iconName="Info" />
                    <div>{t('addStoresModal.stepOne.selectStoresInfo')}</div>
                  </div>
                  <div className={styles.tables}>
                    <TableCardWithSelectedTable
                      isLoading={storesIsLoading}
                      titleOne={t('addStoresModal.stepOne.table.titleOne')}
                      titleTwo={t('addStoresModal.stepOne.table.titleTwo')}
                      data={storesToSelectfrom}
                      columns={[
                        { header: t('addStoresModal.stepOne.table.storeId'), accessorKey: 'storeNo' },
                        {
                          header: t('addStoresModal.stepOne.table.location'),
                          accessorFn: (row: Store) => `${row.address}, ${row.city} ${row.state} ${row.zip}`,
                        },
                      ]}
                      enableMultiRowSelection
                      enableFilters
                      selectedRows={selectedStores}
                      onRowSelectionChange={setSelectedStores}
                    />
                  </div>
                </div>
              </div>
            ),
            title: t('addStoresModal.stepOne.title'),
            description: t('addStoresModal.stepOne.description'),
            onBackClick: handleClose,
            onNextClick: () => {
              setStep(1);
            },
            nextButtonText: t('common.next'),
            nextButtonDisabled: Object.keys(selectedStores).length === 0,
          },
          {
            content: (
              <Grid container direction="column" spacing={3} px="25%">
                <Grid item>
                  <Card>
                    <div className={styles.changeHeaderAndIcon}>
                      <h3 className={styles.titleRow}>{t('addStoresModal.stepTwo.changesHeader')} </h3>
                      <div>
                        <Icon data-testid="Edit2" iconName="Edit2" size="20" onClick={() => setStep(0)} />
                      </div>
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.stepTwoTables}>
                      <Table
                        columns={[
                          {
                            header: t('addStoresModal.stepTwo.tableOne.selectedGroup'),
                            accessorKey: 'groupName',
                          },
                        ]}
                        data={[{ groupName }]}
                        includeMinWidth={false}
                      />
                      <Table
                        includeMinWidth={false}
                        columns={[
                          { header: t('addStoresModal.stepTwo.tableTwo.storeIdsAdded'), accessorKey: 'storeNo' },
                          {
                            header: t('addStoresModal.stepTwo.tableTwo.previousGroup'),
                            accessorKey: 'groupName',
                            cell: alertGroupName(emptyStoreGroups),
                          },
                        ]}
                        data={Object.keys(selectedStores).map((storeNo) => storesToSelectfrom[+storeNo])}
                      />
                    </div>
                  </Card>
                </Grid>
                <Grid
                  item
                  sx={{
                    width: '100%',
                  }}
                >
                  {emptyStoreGroups.length > 0 && (
                    <Chip
                      avatar={
                        <Avatar style={{ width: '35px', height: '35px', backgroundColor: '#FAE4D0', padding: '1px' }}>
                          <AlertTriangle color="#E6740A" />
                        </Avatar>
                      }
                      data-testid="icon"
                      key="emptyStore"
                      label={t('moveStoresModal.groupDeletedWarning')}
                      color="default"
                      variant="outlined"
                      sx={{
                        fontSize: '14px',
                        fontWeight: '500',
                        textAlign: 'left',
                        height: '40px',
                        backgroundColor: '#FAE4D0',
                        color: '#252525',
                        borderColor: '#E6740A',
                        width: '100%',
                        justifyContent: 'left',
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            ),
            title: t('addStoresModal.stepTwo.title'),
            description: t('addStoresModal.stepTwo.description'),
            onBackClick: () => {
              setStep(0);
            },
            onNextClick: () => {
              setOpenConfirmationModal(true);
            },
            nextButtonIsLoading: isLoading,
            nextButtonText: t('addStoresModal.stepTwo.nextButtonText'),
          },
        ]}
        isOpen={isOpen}
        handleClose={handleClose}
      />
      <AddStoresModal
        isOpen={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        selectedStores={Object.keys(selectedStores).map((storeNo) => storesToSelectfrom[+storeNo])}
        currentGroup={groupName}
        allGroups={allGroups}
        currentGroupId={groupId}
        emptyStoreGroups={emptyStoreGroups}
        groupName={groupName}
        onError={onError}
        onSuccess={onSuccess}
      />
      {isModalOpen && <RequestProcessingModal isOpen={isModalOpen} type={modalType} onClose={handleCloseAndRefresh} />}
    </>
  );
};

export default AddStoresToGroup;
